import type { ColDef, ICellRendererParams, IRowNode, ValueGetterParams } from "ag-grid-community";

import { getPropertyByPath } from "../../../propertyPath";

const SERVER_ROW_GROUP = Symbol("serverRowGroup");

export interface ServerRowGroup {
  key: string;
  count: number;
  /** Any row of the group, for rendering the group's value. */
  row: unknown;
}

/** The data of a server-side group row: only the marker, so the group's other cells stay empty. */
export function serverRowGroupData(group: ServerRowGroup): Record<symbol, ServerRowGroup> {
  return { [SERVER_ROW_GROUP]: group };
}

export function serverRowGroupOf(data: unknown): ServerRowGroup | undefined {
  return typeof data === "object" && data !== null
    ? (data as Record<symbol, ServerRowGroup | undefined>)[SERVER_ROW_GROUP]
    : undefined;
}

/** A group row: it has no data of its own (a server-side one only its marker), only the rows grouped under it. */
export function isGroupRow(node: IRowNode | null | undefined): boolean {
  return !!node?.group && (!node.data || !!serverRowGroupOf(node.data));
}

/** The column's value for `data`, outside AG Grid's value service; a string valueGetter is not supported. */
export function cellValue(colDef: ColDef, data: unknown, params: ICellRendererParams): unknown {
  if (typeof colDef.valueGetter === "function") {
    return colDef.valueGetter({
      api: params.api,
      context: params.context,
      node: params.node,
      data,
      colDef,
      column: params.node.rowGroupColumn ?? params.column,
      getValue: (field: string) => getPropertyByPath(data, field),
    } as ValueGetterParams);
  }
  return colDef.field ? getPropertyByPath(data, colDef.field) : undefined;
}
